import { skipToken } from '@reduxjs/toolkit/query';
import { useCreateDialogChip, useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import { draftIdIn } from 'components/create-dialog/useCreateDraft';
import Loader from 'components/Loader';
import { format } from 'date-fns';
import useAuth from 'hooks/useAuth.ts';
import { useMemo, useState } from 'react';
import { useGetContactByIdQuery } from 'store/api/contacts.ts';
import { useGetCorrespondenceByIdQuery } from 'store/api/correspondence.ts';
import { useGetDebriefingByIdQuery } from 'store/api/debriefings';
import { useGetMatterByIdQuery } from 'store/api/matters.ts';
import { useGetTaskByIdQuery } from 'store/api/tasks';
import { useGetCurrentTenantQuery } from 'store/api/tenants.ts';
import { DebriefingType } from 'pages/debriefing/types';
import { applyTaskPrefill, CloseCaseTaskPrefillState } from 'pages/matters/closing/closeCaseModalState';
import { ClientParty } from 'types/matters';
import {
    CorrespondenceDirectionType,
    CorrespondenceEmployeeRoleType,
    CorrespondencePartyRoleType,
} from 'types/correspondence.ts';
import { MinStringOption } from 'types/other';
import { TaskForm, TaskPriorityCode, TaskStatusType } from 'types/tasks';
import { getOptionsFromClassifier } from 'utils/formUtil';
import { DEFAULT_DATE_FORMAT } from 'utils/parseDate';
import { useClassifiers } from 'utils/useClassifiers';
import { TASK_FORM_CLASSIFIERS, taskDtoToForm } from './taskFormMapping';
import TaskPageForm from './TaskPageForm';

const TaskPageAdd = (): JSX.Element => {
    const { user: currentUser } = useAuth();
    const createRoute = useCreateRoute();
    const { contactId, matterId, correspondenceId, debriefingId } = createRoute.params;
    const taskPrefill = (createRoute.location.state as CloseCaseTaskPrefillState | null)?.taskPrefill;

    const {
        data: contact,
        isLoading: isLoadingContact,
        isError: isContactError,
    } = useGetContactByIdQuery(contactId ?? skipToken);

    const {
        data: matter,
        isLoading: isLoadingMatter,
        isError: isMatterError,
    } = useGetMatterByIdQuery(matterId ?? skipToken);

    const {
        data: correspondence,
        isLoading: isLoadingCorrespondence,
        isError: isCorrespondenceError,
    } = useGetCorrespondenceByIdQuery(correspondenceId ?? skipToken);

    const {
        data: debriefing,
        isLoading: isLoadingDebriefing,
        isError: isDebriefingError,
    } = useGetDebriefingByIdQuery(debriefingId ?? skipToken);

    const isPrefillError = isContactError || isMatterError || isCorrespondenceError || isDebriefingError;

    const finalParty = contact ?? matter?.clientParty ?? null;

    useCreateDialogChip({
        parent:
            contact?.name ??
            matter?.fullName ??
            correspondence?.fullName ??
            debriefing?.clientParty?.name ??
            debriefing?.projectName,
    });

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: [
            'TASK_PRIORITY_TYPE',
            'TASK_TYPE',
            'CORRESPONDENCE_TYPE',
            'EVENT_REMINDER_CODE',
            'CORRESPONDENCE_DIRECTION',
            'DMS_DATABASE',
        ],
    });

    const initialData = useMemo<TaskForm | undefined>(() => {
        if (
            classifiers &&
            (!(contactId || matterId) || finalParty) &&
            (!correspondenceId || correspondence) &&
            (!debriefingId || debriefing)
        ) {
            const getClientParties = () => {
                if (correspondence?.parties) {
                    const clients = correspondence.parties
                        .filter((party) => party.roleTypeCode === CorrespondencePartyRoleType.CLIENT)
                        .flatMap((party) => [party.mainParty, party.extraParty])
                        .filter((party): party is NonNullable<typeof party> => party !== null)
                        .map((party) => ({ id: party.id, name: party.name }));
                    if (clients.length > 0) {
                        return clients;
                    }
                }
                // Case/client-specific debriefings seed their client; keeping it in clientParties
                // also protects the prefilled matters from the contact-filter cleanup on change.
                if (debriefing?.clientParty) {
                    return [{ id: debriefing.clientParty.id, name: debriefing.clientParty.name }];
                }
                if (finalParty) {
                    return [{ id: finalParty.id, name: finalParty.name }];
                }
                return [];
            };

            const getMatters = () => {
                if (correspondence?.matters && correspondence.matters.length > 0) {
                    return correspondence.matters.map((m) => ({
                        id: m.id,
                        name: m.name,
                        caseNumber: m.caseNumber,
                        fullName: m.fullName,
                        clientParty: m.clientParty,
                        isConfidential: m.isConfidential,
                        description: '',
                        employees: [],
                        responsibleEmployee: undefined,
                        supervisedByEmployee: undefined,
                    }));
                }
                if (matter) {
                    return [
                        {
                            id: matter.id,
                            name: matter.name,
                            caseNumber: matter.caseNumber,
                            fullName: matter.fullName,
                            clientParty: matter.clientParty,
                            description: matter.description,
                            employees: matter.employees ?? [],
                            responsibleEmployee: matter.responsibleEmployee,
                            supervisedByEmployee: matter.supervisedByEmployee,
                            isConfidential: matter.isConfidential,
                        },
                    ];
                }
                if (debriefing?.debriefingType === DebriefingType.CASE_SPECIFIC && debriefing.clientParty) {
                    // Stored refs carry identity fields only; the client comes from the debriefing root.
                    const debriefingClient = debriefing.clientParty;
                    return (debriefing.matters ?? []).map((debriefingMatter) => ({
                        id: debriefingMatter.id,
                        name: debriefingMatter.name,
                        caseNumber: debriefingMatter.caseNumber,
                        fullName: [debriefingMatter.caseNumber, debriefingMatter.name].filter(Boolean).join(' '),
                        clientParty: { id: debriefingClient.id, name: debriefingClient.name } as ClientParty,
                        isConfidential: debriefingMatter.isConfidential,
                        description: '',
                        employees: [],
                        responsibleEmployee: undefined,
                        supervisedByEmployee: undefined,
                    }));
                }
                return [];
            };

            const form: TaskForm = {
                id: undefined,
                name: '',
                description: '',
                taskStatusTypeCode: TaskStatusType.IN_PROGRESS,
                taskPriority: (
                    getOptionsFromClassifier(classifiers['TASK_PRIORITY_TYPE']) as {
                        label: string;
                        value: string;
                    }[]
                ).find((val) => val.value === TaskPriorityCode.MEDIUM) ?? { label: '', value: '' },
                taskTypes: [],
                assignedToEmployees:
                    correspondence?.directionTypeCode === CorrespondenceDirectionType.INCOMING
                        ? (correspondence?.employees || [])
                              .filter(({ roleTypeCode }) => roleTypeCode === CorrespondenceEmployeeRoleType.RECEIVER)
                              .map(({ employee }) => ({
                                  id: employee.id,
                                  name: employee.name,
                              }))
                        : [],
                assignedByEmployees: [{ id: currentUser?.id ?? 0, name: currentUser?.name ?? '' }],
                clientParties: getClientParties(),
                matters: getMatters(),
                assignments: correspondence?.assignments ?? [],
                correspondence: correspondence
                    ? [
                          {
                              id: correspondence.id,
                              number: correspondence.number,
                              name: correspondence.name,
                              fullName: correspondence.fullName,
                          },
                      ]
                    : [],
                debriefings: debriefing
                    ? [
                          {
                              id: debriefing.id,
                              debriefingType: debriefing.debriefingType,
                              debriefingDate: debriefing.debriefingDate,
                              clientParty: debriefing.clientParty,
                              projectName: debriefing.projectName,
                              isConfidential: debriefing.isConfidential,
                              accessible: debriefing.accessible,
                          },
                      ]
                    : [],
                hasDeadline: false,
                deadlineDate: null,
                isOfficial: correspondence?.isOfficial || false,
                officialReference: correspondence?.officialReference || '',
                notes: '',
                calendarSync: false,
                taskEvent: {
                    id: undefined,
                    inviteSender: undefined,
                    description: '',
                    eventLocation: '',
                    reminder: null,
                    dateStart: format(new Date(), DEFAULT_DATE_FORMAT),
                    timeStart: new Date(),
                    dateEnd: format(new Date(), DEFAULT_DATE_FORMAT),
                    timeEnd: new Date(),
                    eventInvitees: [],
                    allDayMeeting: false,
                },
                dmsData: {
                    dmsLink: correspondence?.dmsLink ?? '',
                    dmsDatabase: undefined,
                    dmsNumber: '',
                },
                replyTo: [currentUser?.workEmailAddress ?? ''],
            };
            return taskPrefill
                ? applyTaskPrefill(
                      form,
                      taskPrefill,
                      getOptionsFromClassifier(classifiers['TASK_TYPE']) as MinStringOption[]
                  )
                : form;
        }
        return undefined;
    }, [
        contactId,
        correspondenceId,
        matterId,
        debriefingId,
        finalParty,
        matter,
        correspondence,
        debriefing,
        classifiers,
        currentUser,
        taskPrefill,
    ]);

    if (
        isLoadingClassifiers ||
        isLoadingContact ||
        isLoadingMatter ||
        isLoadingCorrespondence ||
        isLoadingDebriefing ||
        (initialData === undefined && !isPrefillError)
    ) {
        return <Loader />;
    }

    return <TaskPageForm type="add" classifiers={classifiers} initialData={initialData} />;
};

/** The add form loaded with what a draft holds, read as the task's edit page reads it. */
const TaskDraftAdd = ({ draftId }: { draftId: number }) => {
    const { user: currentUser } = useAuth();
    const [initialData, setInitialData] = useState<TaskForm>();
    const { data: task } = useGetTaskByIdQuery(String(draftId), { refetchOnMountOrArgChange: true });
    const { data: tenant, isLoading: isLoadingTenant } = useGetCurrentTenantQuery();
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: TASK_FORM_CLASSIFIERS });

    // Frozen once loaded: a refetch must not reset what the user is typing.
    if (!initialData && task && currentUser && !isLoadingClassifiers && !isLoadingTenant) {
        setInitialData(taskDtoToForm(task, { classifiers, currentUser, emailOrganizer: tenant?.emailOrganizer }));
    }

    return initialData ? <TaskPageForm type="add" classifiers={classifiers} initialData={initialData} /> : <Loader />;
};

/** The task create; its URL with `?draft=<id>` edits that draft instead of a blank task. */
const TaskPageAddOrDraft = () => {
    const draftId = draftIdIn(useCreateRoute().location.search);
    return draftId === null ? <TaskPageAdd /> : <TaskDraftAdd key={draftId} draftId={draftId} />;
};

export default TaskPageAddOrDraft;
